import { mergeIntoMain, multiWindow, onMessageLink, onReplicated, report, windowRole, updates } from "@transport";
import { useEffect, useRef } from "preact/hooks";

import * as updateWatch from "./lib/update-watch";

import * as actions from "./lib/actions";
import * as auth from "./lib/auth";
import * as backstop from "./lib/backstop";
import * as delta from "./lib/delta";
import * as events from "./lib/events";
import * as nav from "./lib/navigation";
import * as panes from "./lib/panes";
import * as push from "./lib/push";
import * as retention from "./lib/retention";
import * as shortcuts from "./lib/shortcuts";
import * as store from "./lib/store";
import * as tabs from "./lib/tabs";
import * as compose from "./lib/compose";
import * as composeWindow from "./lib/composeWindow";
import * as tabview from "./lib/tabview";
import type { Envelope } from "./lib/types";
import { openForward, openReply, openReplyAll } from "./ui/Compose";
import * as dialog from "./ui/dialog";
import * as Icon from "./ui/Icons";
import { InstallHint } from "./ui/InstallHint";
import { Login } from "./ui/Login";
import { MessageList } from "./ui/MessageList";
import { PaneDivider } from "./ui/PaneDivider";
import { Reader } from "./ui/Reader";
import { AttachmentView } from "./ui/AttachmentView";
import { MessageWindow } from "./ui/MessageWindow";
import { ComposeWindow, ComposeWindowFailed } from "./ui/ComposeWindow";
import { SettingsShell } from "./ui/SettingsShell";
import { TabBar } from "./ui/TabBar";
import { Sidebar } from "./ui/Sidebar";
import { Overlays } from "./ui/Overlays";
import { ResizeGrips, WindowControls } from "./ui/WindowControls";
import { ServerAddress } from "./ui/ServerAddress";

/**
 * The shortcut sheet's open flag, which used to be a `signal` in this file.
 *
 * It moved next to the pane state because the back button has to be able to
 * close it: everything the app stacks over the message list is one list, and a
 * layer that is not in that list is a layer the system back button walks past
 * on its way out of the site.
 */
const helpOpen = nav.helpOpen;

export function App() {
  // Begin the background update watch (desktop only; a browser page is
  // already the newest version of itself). Idempotent, so StrictMode-style
  // double-mounts cost nothing.
  useEffect(() => {
    updateWatch.start(updates);
  }, []);

  useStartup();
  useKeyboard();
  useLiveUpdates();
  useFocusOnPaneChange();

  // Gate on what the server says, not on whether a string in localStorage is
  // non-empty. The old test could not tell a revoked credential from a good
  // one, so an expired session rendered as a working mail UI with a red banner
  // and no way to sign in again.
  switch (auth.authState.value) {
    case "unknown":
      return <Booting />;
    case "out":
      return <Login />;
    case "unreachable":
      return <Unreachable />;
    case "in":
      break;
  }

  /*
   * A detached window shows one message and none of the shell.
   *
   * Two ways to be one, and they are different facts. On the desktop it is the
   * window's label, which Rust set before this webview loaded -- so a reload
   * cannot turn a message window into a second main window. In a browser there
   * is no label and nothing but the URL, so it is `#/read/<account>/<id>`, read
   * once at startup by `lib/navigation.ts`.
   *
   * Kept as two conditions rather than one signal that tries to mean both. The
   * transport answers what kind of window the platform gave us; navigation
   * answers what this page was asked to show. Collapsing those is what put a
   * tab strip in the browser.
   *
   * After the auth switch above, deliberately: a detached window whose session
   * has been revoked should show the login screen like anything else, rather
   * than a reader that can never load.
   */
  if (windowRole() === "message" || nav.solo.value) return <MessageWindow />;

  /*
   * A compose window holds one message being written and none of the shell.
   *
   * The same pair of conditions as the reader above, meaning the same two
   * different things: the window's label, which Rust set before this webview
   * loaded, and the address the page was loaded at. Both are checked because
   * neither implies the other -- and collapsing them is the mistake that put a
   * tab strip in the browser once already.
   *
   * `composeSolo` is what makes `#/compose/...` typed into a browser render as
   * a composer rather than as the mail with a composer over it. Nothing in the
   * browser build produces that address, so this is the honest answer to a URL
   * somebody constructed rather than a feature: the page was asked for a
   * composer, so it shows one.
   */
  if (windowRole() === "compose" || nav.composeSolo.value !== null) {
    const failed = composeWindow.openFailure.value;
    return failed === null ? <ComposeWindow /> : <ComposeWindowFailed why={failed} />;
  }

  // Read from `lib/panes.ts` rather than computed here, because the widths that
  // module hands the grid depend on the same answer: with no reading pane the
  // middle column stretches, and a divider that reserved room for a column that
  // is not there would shrink the sidebar for nothing.
  const showPane = panes.readerColumn.value;
  /** True while a message tab is in front, so the reader has the window. */
  const solo = !tabs.isPinned(tabs.current.value);
  /** The attachment the tab in front is showing, if it is showing one. */
  const viewing = solo ? (tabs.current.value.address?.part ? tabs.current.value.address : null) : null;

  return (
    <>
      {/*
        Every pane is rendered on every width. Which ones are on screen is the
        stylesheet's business, keyed off `data-pane` -- a narrow layout that
        unmounted the panes it is not showing would throw away the message
        list's scroll position every time someone opened a message, and would
        re-fetch the body every time they came back.
      */}
      {/*
        Above the shell, because it is chrome for the window rather than a pane
        inside it -- and because the mail tab has to sit over the sidebar, which
        is where its own column starts. Draws nothing until there is a second
        tab. Desktop only in practice: nothing opens one in a browser.
      */}
      {/*
        The window's own titlebar controls, and the edges that size it.

        The controls are pinned to the corner only when there is no tab strip:
        the strip is full width and at the top of the window, so when it exists
        it carries them as ordinary flex children and they align with the tab
        menu for free. Without it the top-right belongs to whichever of five
        elements reaches the edge, so they are fixed and the space is reserved
        by `--chrome-right` in the stylesheet instead.

        Both draw nothing at all unless this build owns the chrome.
      */}
      {!tabs.hasTabs.value && <WindowControls />}
      <ResizeGrips />
      <TabBar />
      {/*
        A message tab takes the whole window under the strip: the reader fills
        it and the sidebar and list are hidden.

        Hidden, not unmounted, and that is the whole point of doing it this way.
        The shell's own comment says a layout that unmounts the panes it is not
        showing throws away the message list's scroll position -- and that is
        exactly what an earlier version of this did, by rendering a bare reader
        instead of the shell. Coming back to the mail put somebody at the top of
        their inbox every time.

        So this reuses the machinery that already solves it: one grid cell,
        `visibility` deciding which pane is seen, and every pane still mounted
        with its scroll offset intact.
      */}
      <div
        class={`shell${showPane ? "" : " no-pane"}${solo ? " is-solo" : ""}`}
        data-pane={solo ? "reader" : nav.pane.value}
      >
        <Sidebar />
        {/*
          Each divider goes between the two columns it separates rather than
          after all of them: it is where a keyboard expects to meet it on the
          way across the app, and it keeps the last column last, which is what
          `.col:last-child` uses to drop the trailing border. The dividers take
          no grid track of their own -- they are positioned over the edge, and
          the stylesheet leaves them out entirely below three columns.
        */}
        <PaneDivider edge="sidebar" />
        {store.view.value === "mail" ? (
          <>
            <MessageList />
            {showPane && <PaneDivider edge="list" />}
            {/*
              An attachment tab shows the attachment where the reader would be.

              Decided here rather than inside the reader because it is a
              question about which tab is in front, and the reader is also what
              a *window* renders -- where there are no tabs to ask about. `solo`
              is already the answer to "is a tab in front"; this reads the same
              tab for what kind it is.

              `solo` and not `tabs.current` read inside the reader: see the
              prop's own note for the window that would get it wrong.
            */}
            {viewing ? <AttachmentView address={viewing} /> : <Reader inTab={solo} />}
          </>
        ) : (
          /*
            One panel where there were two. Accounts is not a view of its own
            any more -- it is the first page of the settings shell, and
            `lib/navigation.ts` turns the older `view = "accounts"` into that,
            so the sidebar and the empty mailbox still reach it by name. The
            head and the back button belong to the shell now, because on a
            narrow screen both depend on which page is open.
          */
          <section class="col panel-col" data-pane-name="panel" tabIndex={-1} aria-label="Settings">
            <SettingsShell />
          </section>
        )}
      </div>

      {/* Below the compose window and the shortcut sheet in the tree, and
          therefore under them on screen: an offer to install must never be over
          something someone is typing into. It is also the one overlay that does
          not belong to `Overlays`, because a detached message window is not
          where anybody decides to install an app. */}
      <InstallHint />
      <Overlays />
    </>
  );
}

/**
 * Establish who we are, then load their mail.
 *
 * Two steps and in this order, because they answer different questions and the
 * second is pointless without the first. `refreshIdentity` asks the server
 * whether this browser still has a usable credential; only then is there any
 * point fetching a session document.
 */
function useStartup(): void {
  useEffect(() => {
    void auth.refreshIdentity();
  }, []);

  useEffect(() => {
    if (auth.authState.value === "in" && !store.session.value) void store.connect();
  }, [auth.authState.value]);

  // A notification click may have launched the app, in which case the message
  // it named has been waiting since before there was anyone to open it for.
  // Spent here rather than where it arrives, because this is the first moment
  // an authenticated JMAP call can succeed.
  const session = store.session.value;
  const waiting = push.pending.value;
  useEffect(() => {
    if (session && waiting) push.takePending();
  }, [session, waiting]);

  // Which messages are kept offline, once there is a session to name accounts
  // from. A no-op in the browser, where there is no local store -- so this is
  // one call rather than a branch, the same arrangement as `replicate`.
  //
  // A pin changes when somebody presses the control, which updates the set
  // itself -- and the other way, when a resync destroys a message and takes its
  // pin with it. That second case used to leave a badge claiming a message was
  // kept offline until the next launch, on the grounds that the alternative was
  // a poll.
  //
  // It was not. The replication pass already counts what it destroyed, so it
  // can say so: `onReplicated` fires once per run and only when rows actually
  // went away, which is both narrower than a poll and more prompt than one.
  // A no-op in the browser, which has no replica and nothing that can retire a
  // pin behind the interface's back.
  useEffect(() => {
    if (!session) return;
    void retention.load();
    onReplicated(() => void retention.load());
  }, [session]);

  // A message named by the URL this app was loaded on: a reload with one open,
  // or a link somebody followed in. Same moment and same reason as the pending
  // notification above -- an address can be adopted before there is an account
  // to resolve it against.
  //
  // After it, and only when it did not fire, because the two can both be set
  // and they are not equal claims: a notification is something the person did a
  // second ago, and the URL is where they happened to be beforehand.
  useEffect(() => {
    if (!session || waiting) return;
    const named = nav.takePendingMessage();
    if (named) void store.openById(named.account, named.email);
    // A `mailto:` from a cold launch, held until there was a session to open a
    // composer over -- see `nav.pendingMailto`.
    const mailto = nav.takePendingMailto();
    if (mailto) compose.openMailto(mailto);
  }, [session, waiting]);

  // The composer this window was opened for, filled in from the same address
  // the window was loaded at. Same moment and same reason as the message above:
  // a reply names a parent and a resumed draft names itself, and neither can be
  // fetched before there is a session to fetch it with.
  //
  // The failure is held rather than thrown away. In the main window a composer
  // that would not open leaves the mail on screen; in a compose window it would
  // leave an empty frame, so `ui/ComposeWindow.tsx` needs the reason.
  useEffect(() => {
    if (!session || waiting) return;
    const asked = nav.takePendingCompose();
    if (!asked) return;
    void composeWindow.applyRequest(asked).catch((e: unknown) => {
      composeWindow.openFailure.value = e instanceof Error ? e.message : String(e);
    });
  }, [session, waiting]);

  // Links handed over by the operating system: a tapped notification, or
  // `hydramail://` followed from elsewhere on the machine.
  //
  // Subscribed once rather than per session, because the subscription is how
  // the transport delivers a link that arrived before the app finished
  // loading -- resubscribing would collect it a second time. A link that
  // arrives while signed out is parked in `pendingMessage` and spent by the
  // effect above once there is a session, which is the same path a URL takes.
  // Keep the open message and the front tab in step. Started once and never
  // stopped; see `lib/tabview.ts` for why tabs multiplex one reader.
  useEffect(() => tabview.watch(), []);

  // Tabs from last time. Restored once, before anything can open one, so a tab
  // opened during startup is not overwritten by what was on disk.
  //
  // Not in a browser, where there are no tabs to restore. Storage is per origin
  // and this build never writes any -- but it is the same origin the desktop
  // dev build uses, and a key left there by one must not raise a strip in the
  // other.
  useEffect(() => {
    if (multiWindow) tabs.restore();
  }, []);

  useEffect(() => {
    onMessageLink((link) => {
      // A `mailto:` -- HydraMail is the OS's email app -- opens the composer;
      // a `hydramail://message` opens the message. Tried in that order because
      // only one can match, and both arrive here unparsed on purpose (see
      // `lib/net.types.ts`).
      const mailto = nav.mailtoFromLink(link);
      if (mailto) {
        if (store.session.peek()) compose.openMailto(mailto);
        else nav.pendingMailto.value = mailto;
        return;
      }
      const named = nav.messageFromLink(link);
      // A link naming nothing valid opens nothing -- refused here.
      if (!named) return;
      if (store.session.peek()) void store.openById(named.account, named.email);
      else nav.pendingMessage.value = named;
    });
    // The buttons on a desktop notification, which act without opening
    // anything. A no-op in the browser. See `lib/push.ts`.
    push.watchToastActions();
  }, []);

  // A page that dies in the shipped webview dies silently: there is no
  // inspector to open, so an exception or an abandoned promise simply becomes
  // "the app is doing nothing" -- which took an afternoon to see through, once.
  // Both are forwarded to the shell's log, where journalctl can read them.
  useEffect(() => {
    const onError = (e: ErrorEvent) =>
      report(`unhandled error: ${e.message} (${e.filename}:${e.lineno})`);
    const onRejection = (e: PromiseRejectionEvent) => {
      const why = e.reason instanceof Error ? (e.reason.stack ?? e.reason.message) : String(e.reason);
      report(`unhandled rejection: ${why}`);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
}

/**
 * The first paint, before the server has answered.
 *
 * Deliberately not the login form. Flashing a password prompt at someone who is
 * already signed in, for the third of a second it takes to ask, is the kind of
 * detail that makes an app feel unreliable.
 */
function Booting() {
  return (
    <div class="login">
      <div class="login-card" style={{ textAlign: "center" }}>
        <span class="spinner" />
      </div>
    </div>
  );
}

/**
 * The daemon did not answer.
 *
 * Distinct from the login screen on purpose: a password box shown to someone
 * whose password could not possibly have been checked invites them to conclude
 * they typed it wrong and go looking for a mistake that is not theirs.
 */
function Unreachable() {
  return (
    <div class="login">
      <div class="login-card">
        <div class="login-brand">
          <Icon.Warning />
          <strong>Cannot reach the daemon</strong>
        </div>
        <p class="lede">
          This page loaded, so the web server is up — hydramaild behind it is not answering. Check
          that it is running, then try again.
        </p>
        {/* The reason this screen carries it: on a packaged build the default
            address is `localhost`, which on a phone is the phone. A new install
            lands here, and this is the fix -- behind a settings page it could
            not be reached, because this screen is what stands in front of the
            app. */}
        <ServerAddress />
        <button class="btn primary" onClick={() => void auth.refreshIdentity()}>
          Try again
        </button>
      </div>
    </div>
  );
}

/**
 * What a one-key action acts on.
 *
 * The ticked messages if there are any, otherwise the row under the cursor.
 * That order matters: someone who has ticked twelve rows and presses `e` means
 * the twelve, and a shortcut that quietly archived only the one under the
 * cursor would be the most annoying possible way to be wrong.
 */
function keyTargets(): Envelope[] {
  const selected = store.selectedRows();
  if (selected.length > 0) return selected;
  const row = store.cursorRow() ?? store.openEmail.value;
  return row ? [row] : [];
}

/**
 * Open the composer on one message: reply, reply-all, forward.
 *
 * Deliberately not built on {@link keyTargets}. Every other shortcut scales to
 * a selection and this one does not -- there is no reply to forty messages --
 * so it takes the open message, or the row under the cursor when the reading
 * pane is empty, and a selection is beside the point either way.
 *
 * It refuses outright while the composer is open. There is one composer window
 * and one draft in it: a stray Shift+R with focus on the Attach button would
 * otherwise replace a half-written message with a quote of somebody else's,
 * with the only copy of the text being whatever the autosave had reached.
 */
function openWriting(open: (row: Envelope) => void): void {
  if (store.composeOpen.value) return;
  const row = store.openEmail.value ?? store.cursorRow();
  if (row) open(row);
}

/**
 * How long `f` waits for the second key of a filter chord.
 *
 * A chord rather than four more single letters, because every letter that would
 * have been mnemonic is taken: `s` flags, `Shift+U` marks unread, `e` archives.
 * Adding a filter on `Shift+S` next to a flag on `s` is the kind of near-miss
 * that gets someone's mail archived when they meant to look at it.
 *
 * While a chord is pending the next key is consumed whether or not it completes
 * one -- `f` then `e` filters nothing and archives nothing. Swallowing a
 * keystroke is a nuisance; archiving a message because a chord fell through is
 * not, and the window is short enough that nobody arrives in it by accident.
 */
const CHORD = 1500;

/**
 * What each shortcut does. Which keys press it, and what the help sheet calls
 * it, are in `lib/shortcuts.ts`.
 *
 * A `Record` over the id union rather than the `switch` this used to be, and
 * that is the entire point of the split: a shortcut in the table with nothing
 * here, or a handler here naming a shortcut the table does not have, is a
 * compile error. The help screen and the keyboard cannot drift apart again
 * without the build saying so -- which is what phase 9 wanted and what nine
 * hand-copied rows had already failed to do.
 *
 * Returning `false` **declines** the key: the shortcut does not apply right
 * now, nothing is prevented, and the event goes on to whatever the browser or
 * the OS would have done with it. Only the two desktop keys need it, and both
 * need it badly -- `Ctrl+W` pressed on the mail tab must stay the browser's own
 * close, and `Ctrl+Shift+M` outside a message window belongs to whoever else
 * wants it.
 */
const RUN: Record<shortcuts.ShortcutId, () => boolean | void> = {
  next: () => store.step(1),
  prev: () => store.step(-1),
  open: () => {
    const row = store.cursorRow();
    // Not re-opened if it already is: on a narrow screen `o` is how you get
    // back to the reading pane, and re-fetching the body to do that would blank
    // it for the length of a round trip.
    //
    // Through `activate` rather than `store.open`, so that `Enter` on a draft
    // resumes it exactly as clicking it does. This is the deliberate-activation
    // path; `next` and `prev` below still call `store.step`, which must keep
    // opening rows in the reader as the cursor passes over them.
    if (row && store.openEmailId.value !== store.keyOf(row)) void composeWindow.activate(row);
    else if (!store.openEmail.value) store.step(1);
  },
  // The same one step back the system back button takes, so the two cannot
  // disagree about what is on screen.
  back: () => nav.back(),
  search: () => document.querySelector<HTMLInputElement>('input[type="search"]')?.focus(),
  escape: () => {
    /*
     * Three things Escape can mean, in the order they were most recently asked
     * for. Clearing a selection first is what makes ticking forty rows feel safe
     * to try. A sheet over the app is not in this list -- it takes its own
     * Escape before the event reaches here.
     *
     * **In a message tab it does nothing**, in a detached window it does
     * nothing, and in a browser tab opened at `#/read/` it does nothing. Amit's call, and the rule behind it is that a tab is closed with
     * the mouse on its close button and by nothing else -- a keystroke that
     * takes a whole view away is too easy to press by accident and there is no
     * undo for it.
     *
     * Backing out of the reader would be no better: the message *is* the tab, so
     * it would leave the tab in front holding nothing. The tab re-asserts its
     * message if anything else clears it, which covers the routes Escape is not
     * on -- archiving from the reading bar, a filter dropping the row. See
     * `lib/tabview.ts`.
     *
     * A window is the OS's object besides, and the title bar is how one is
     * closed.
     */
    // A tab or a strip control that was *clicked* keeps focus, and this
    // keystroke is what makes the browser draw a ring round it -- so Escape left
    // a blue outline on whatever was last pressed. Dropped rather than styled
    // away, so somebody who tabbed there still gets the ring. See
    // `dropFocusRing` in `ui/TabBar.tsx` for the same fix on the menu's own
    // Escape.
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && focused.closest(".tab-bar")) focused.blur();

    if (store.selectionCount.value > 0) store.clearSelection();
    // The third clause is the browser's standalone reader, and it is there for
    // exactly the reason the second one is: `nav.back()` closes the reader
    // layer, and on a page that is only a message that leaves a page that is
    // nothing. Amit reported this against tabs; a `#/read/` tab is the same
    // view reached a different way and would have grown the same bug.
    else if (
      windowRole() !== "message" &&
      !nav.solo.value &&
      tabs.isPinned(tabs.current.value)
    )
      nav.back();
  },
  refresh: () => void store.refresh(),

  select: () => {
    const row = store.cursorRow();
    if (row) store.toggleSelect(row);
  },
  archive: () => void actions.moveToRole(keyTargets(), "archive"),
  trash: () => void actions.moveToRole(keyTargets(), "trash"),
  flag: () => {
    const rows = keyTargets();
    const allFlagged = rows.length > 0 && rows.every((r) => r.email.keywords["$flagged"]);
    void actions.markFlagged(rows, !allFlagged);
  },
  // The other half of Shift+U, and the reason it is worth a key at all: a
  // mailbox triaged with `j` and a read delay of four seconds fills with
  // messages that were looked at and never marked.
  read: () => void actions.markSeen(keyTargets(), true),
  unread: () => void actions.markSeen(keyTargets(), false),

  compose: () => void composeWindow.writeNew(),
  // Shift, on all three, because the unshifted letters are spoken for: `r`
  // refreshes, `f` opens a filter chord, and `a` is that chord's second key.
  // Moving `r` to reply to match every other mail client would put a compose
  // window one fumbled keystroke from a refresh, which is the sort of near-miss
  // the chord exists to avoid.
  reply: () => openWriting(openReply),
  replyAll: () => openWriting(openReplyAll),
  forward: () => openWriting(openForward),

  filterUnread: () => store.toggleFilter("unread"),
  filterFlagged: () => store.toggleFilter("flagged"),
  filterAttachment: () => store.toggleFilter("attachment"),
  filtersClear: () => store.clearFilters(),

  /*
   * Closing a tab, which is one of the two modified shortcuts this app takes.
   *
   * Ctrl+W is what every tabbed application means by "close this", so a mail
   * client with tabs that ignored it would be the odd one. Escape deliberately
   * does not do this -- it is far too easy to press by accident for something
   * with no undo, and that is Amit's rule. Ctrl+W is a two-finger act of intent,
   * which is the difference.
   *
   * Declined unless a message tab is in front, so the mail tab is safe and the
   * browser build -- which has no tabs, and where the browser reserves this key
   * anyway -- never reaches the `preventDefault`.
   */
  closeTab: () => {
    const front = tabs.current.value;
    if (!tabs.isPinned(front)) {
      tabs.close(front.id);
      return;
    }
    // The pinned main tab has nothing to close -- but if a message is open in
    // its reading pane, that is the thing the reader means by "close what I am
    // looking at", so Ctrl+W closes it, the same as the back button and Escape.
    // Nothing open: decline, and let the platform have the key.
    if (store.openEmailId.value !== null) {
      store.closeEmail();
      return;
    }
    return false;
  },

  /*
   * Putting a detached message back, which is the keyboard's half of the button
   * in `ui/MessageWindow.tsx`.
   *
   * Shift as well, so it cannot be confused with the `Ctrl+W` above -- these are
   * opposite intentions on adjacent fingers, and one of them discards a view.
   *
   * Declined outside a message window. In the main window there is nothing to
   * merge, and swallowing the key there would take it from whatever the OS does
   * with it.
   */
  reattach: () => {
    if (windowRole() !== "message") return false;
    void mergeIntoMain().catch((err: unknown) => {
      store.toast(
        `Could not move this message back — ${err instanceof Error ? err.message : String(err)}`,
        "error",
      );
    });
  },

  // Only ever opens. The sheet is modal from the moment it is up, so a second
  // `?` never reaches this handler; writing it as a toggle would be describing a
  // behaviour that cannot happen.
  help: () => {
    helpOpen.value = true;
  },
};

function useKeyboard(): void {
  useEffect(() => {
    let chord = false;
    let chordTimer: ReturnType<typeof setTimeout> | undefined;
    const endChord = () => {
      chord = false;
      clearTimeout(chordTimer);
    };

    const onKey = (e: KeyboardEvent) => {
      // A sheet over the app owns the keyboard for as long as it is up, and it
      // brings its own Escape and its own Tab (see `ui/dialog.ts`). Nothing
      // below this line is about what is on screen at that moment: `e` pressed
      // while reading the list of what the keys do would archive the message
      // behind the sheet, and nothing visible would say that it had.
      if (dialog.modalOpen.value) {
        endChord();
        return;
      }

      const target = e.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;

      if (typing) {
        // Escape gets you out of the search box; nothing else applies while
        // the keystroke is part of a word someone is writing. Without this
        // every `e` in a subject line would archive something.
        if (e.key === "Escape") target?.blur();
        endChord();
        return;
      }

      /*
       * The second key of a chord, which suspends every other shortcut while it
       * is pending.
       *
       * The key is taken whether or not it completes one -- `f` then `e` filters
       * nothing *and archives nothing*. Swallowing a keystroke is a nuisance;
       * archiving a message because a chord fell through is not.
       *
       * A modified key is the exception, because holding Ctrl is a statement
       * that you meant something else entirely. It ends the chord and carries on
       * to the dispatch below, so Ctrl+W within a second of pressing `f` still
       * closes the tab rather than vanishing.
       */
      if (chord) {
        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
          endChord();
          e.preventDefault();
          const hit = shortcuts.match(e, true);
          if (hit) RUN[hit.id]();
          return;
        }
        endChord();
      }

      // Enter belongs to whatever has focus, if that thing already does
      // something with it. A button, a link or a `<summary>` activates on Enter
      // by itself, and letting the shortcut run as well opened the message under
      // the cursor *behind* the control someone had just pressed -- which then
      // marked it read a second and a half later. Pressing Done on the filter
      // panel is the way anyone meets this, but it was never specific to that:
      // Compose, Refresh and every chip before them did the same thing.
      //
      // Only Enter, and only for controls the browser activates: `o` still opens
      // the cursor row from anywhere, and a focused list row keeps working
      // because activating the row is what the shortcut would have done anyway.
      if (
        e.key === "Enter" &&
        (target instanceof HTMLButtonElement ||
          target instanceof HTMLAnchorElement ||
          target instanceof HTMLSelectElement ||
          target?.tagName === "SUMMARY")
      ) {
        return;
      }

      // `f` on its own arms the chord and waits for the second key.
      if (shortcuts.opensChord(e)) {
        e.preventDefault();
        chord = true;
        chordTimer = setTimeout(endChord, CHORD);
        return;
      }

      /*
       * Everything else, straight off the table.
       *
       * A shortcut with a modifier the table did not ask for matches nothing and
       * falls out here untouched, which is the rule the old `switch` needed a
       * guard for: Ctrl+R is reload, Cmd+E is a find bar, and swallowing either
       * to archive mail is the kind of thing that gets a client uninstalled.
       */
      const hit = shortcuts.match(e, false);
      if (!hit) return;
      if (RUN[hit.id]() === false) return;
      if (hit.preventDefault) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      endChord();
    };
  }, []);
}

/**
 * Put focus in the pane that just arrived, on a layout that shows one at a
 * time.
 *
 * Without this the pane changes and focus stays on a control that is no longer
 * rendered, which sends focus back to the top of the document -- so a screen
 * reader announces nothing at all about the message that just opened, and the
 * next Tab starts from the first thing on the page rather than from the thing
 * the reader is looking at.
 *
 * The pane container takes focus rather than the first control in it, because
 * the container carries the label ("Message", "Messages", "Mailboxes") and the
 * label is the sentence worth hearing. It is skipped on the three-column layout
 * for the opposite reason: nothing has left the screen there, so moving focus
 * would be taking it away from whatever the reader was using.
 */
function useFocusOnPaneChange(): void {
  const shown = useRef<nav.Pane | null>(null);
  const pane = nav.pane.value;
  useEffect(() => {
    const first = shown.current === null;
    if (shown.current === pane) return;
    shown.current = pane;
    if (first || window.matchMedia(nav.THREE_COLUMN).matches) return;
    document.querySelector<HTMLElement>(`[data-pane-name="${pane}"]`)?.focus();
  }, [pane]);
}

/**
 * How often the backstop below runs. Five minutes, and read the comment there
 * before shortening it.
 */
const BACKSTOP = 5 * 60_000;

/**
 * Stay up to date: a live stream, and a slow timer behind it.
 *
 * The stream is the mechanism -- `lib/events.ts` holds the whole of it, and the
 * reasoning about `EventSource` with it. This file's business is when it runs
 * and what an event means: it runs while someone is signed in, and an event
 * means *ask what changed*, which is `lib/delta.ts`.
 *
 * It used to mean re-read the session, every mailbox and the whole list, on
 * every event, because the event says only that something moved. That is about
 * 139 KB, and the daemon polls one shared counter every two seconds, so a
 * backfill of an archive nobody is looking at cost roughly 250 MB an hour to
 * learn nothing. The delta path asks `Email/queryChanges` and `Mailbox/changes`
 * instead, and refetches only what the answer says is on screen. The reason the
 * session is still re-read at all is unchanged and still matters -- accounts
 * are added with the CLI, and a client that only re-ran `Email/query` would
 * leave the sidebar empty until someone reloaded the page -- but it now happens
 * when a folder appears or disappears rather than every two seconds.
 *
 * # Why there is still a timer
 *
 * This used to be a one-minute poll and it is not one now. What remains is a
 * backstop, and it exists because every way a stream can fail *loudly* is
 * already handled in `events.ts` -- what it cannot handle is a stream that is
 * open and dead. A proxy that drops the connection without closing it, a laptop
 * resumed from sleep with a socket that will never carry another byte, a mobile
 * browser that froze the tab: in every one of those the client believes it is
 * connected, and nothing else in the app would ever ask again. A mail client
 * that can wedge itself into never updating is the thing a person actually
 * objects to; five minutes is slow enough that nobody would call it polling and
 * fast enough that the wedge lasts minutes rather than the rest of the day.
 *
 * It deliberately does not skip when the stream is live, because "the stream is
 * live" is exactly the claim it is there to check.
 *
 * For a long time it skipped on *visibility* instead, which is the same
 * assumption in different clothes -- and worse here, because a minimised window
 * is the ordinary state of a desktop mail client and precisely when a
 * notification is the only way to hear about mail. It also refreshed the view
 * without replicating, and replication is the only thing that can notify. Both
 * are fixed; `lib/backstop.ts` says why at length.
 */
function useLiveUpdates(): void {
  const signedIn = auth.authState.value === "in";
  useEffect(() => {
    if (!signedIn) return;
    // Closed on the way out, which is what makes signing out close the socket
    // rather than leave a revoked session connected.
    return events.connect(() => delta.sync());
  }, [signedIn]);

  useEffect(() => {
    const tick = () => {
      // What to do is `lib/backstop.ts`, which exists so that decision can be
      // tested without rendering an app and waiting five minutes. Carrying it
      // out is this file's business, and stays here.
      const actions = backstop.plan({
        auth: auth.authState.value,
        hasSession: Boolean(store.session.value),
        visible: document.visibilityState === "visible",
      });
      for (const action of actions) {
        switch (action) {
          case "refresh-identity":
            void auth.refreshIdentity();
            break;
          case "replicate":
            delta.replicateAll();
            break;
          case "connect":
            void store.connect();
            break;
          case "refresh-view":
            void store.refresh({ quiet: true });
            break;
        }
      }
    };
    const timer = setInterval(tick, BACKSTOP);
    return () => clearInterval(timer);
  }, []);
}
